import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const RegistrarVehiculo = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastBitacoraId, setLastBitacoraId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [vehiculoInfoModal, setVehiculoInfoModal] = useState({ placa: '', nombre: '', apellido: '' });

  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [placa, setPlaca] = useState('');
  const [autorizadoPor, setAutorizadoPor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [firmaArchivo, setFirmaArchivo] = useState(null);
  const [firmaPreview, setFirmaPreview] = useState('');
  const [isFirmaModalOpen, setIsFirmaModalOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [firmaTieneTrazo, setFirmaTieneTrazo] = useState(false);
  const [requiereFirma, setRequiereFirma] = useState(true); // Por defecto requiere firma
  const [requiereFoto, setRequiereFoto] = useState(true); // Por defecto requiere foto
  const canvasRef = useRef(null);
  const capturaInputId = useMemo(() => `vehiculo-foto-captura-${uuidv4()}`, []);
  
  // Estados para autocompletado del catálogo de funcionarios (Persona que autoriza)
  const [catalogoFuncionarios, setCatalogoFuncionarios] = useState([]);
  const [sugerenciasAutorizado, setSugerenciasAutorizado] = useState([]);
  const [mostrarSugerenciasAutorizado, setMostrarSugerenciasAutorizado] = useState(false);
  const sugerenciasAutorizadoRef = useRef(null);

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  // Cargar catálogo de funcionarios
  const fetchCatalogoFuncionarios = useCallback(async () => {
    if (!user?.id_generado) return;

    try {
      const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO'));
      url.searchParams.set('id_generado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Filtrar solo funcionarios
        const funcionarios = (data.data || []).filter(item => item.tipo === 'FUNCIONARIO');
        setCatalogoFuncionarios(funcionarios);
      }
    } catch (err) {
      console.error('Error cargando catálogo de funcionarios:', err);
    }
  }, [user?.id_generado]);

  // Cargar catálogo al montar el componente
  useEffect(() => {
    fetchCatalogoFuncionarios();
  }, [fetchCatalogoFuncionarios]);

  // Filtrar sugerencias de "Persona que autoriza" mientras se escribe
  useEffect(() => {
    if (autorizadoPor.trim().length >= 2) {
      const textoBusqueda = autorizadoPor.trim().toLowerCase();
      const filtradas = catalogoFuncionarios.filter(funcionario => {
        const nombreCompletoFuncionario = funcionario.Dato?.toLowerCase() || '';
        return nombreCompletoFuncionario.includes(textoBusqueda);
      });
      
      // Verificar si el texto coincide exactamente con alguna sugerencia
      const coincideExactamente = filtradas.some(funcionario => 
        funcionario.Dato?.toLowerCase() === textoBusqueda
      );
      
      setSugerenciasAutorizado(filtradas);
      // No mostrar sugerencias si coincide exactamente o si no hay coincidencias
      setMostrarSugerenciasAutorizado(filtradas.length > 0 && !coincideExactamente);
    } else {
      setSugerenciasAutorizado([]);
      setMostrarSugerenciasAutorizado(false);
    }
  }, [autorizadoPor, catalogoFuncionarios]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sugerenciasAutorizadoRef.current && !sugerenciasAutorizadoRef.current.contains(event.target)) {
        setMostrarSugerenciasAutorizado(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSeleccionarSugerenciaAutorizado = (funcionario) => {
    const nombreSeleccionado = funcionario.Dato || '';
    setAutorizadoPor(nombreSeleccionado);
    // Cerrar inmediatamente y limpiar sugerencias
    setMostrarSugerenciasAutorizado(false);
    setSugerenciasAutorizado([]);
    // Limpiar errores relacionados
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.autorizadoPor;
      return newErrors;
    });
    // Hacer focus en el input para que el usuario pueda continuar
    setTimeout(() => {
      const input = document.getElementById('vehiculo-autorizado');
      if (input) {
        input.focus();
        // Mover el cursor al final
        input.setSelectionRange(nombreSeleccionado.length, nombreSeleccionado.length);
      }
    }, 0);
  };

  const resetForm = useCallback(() => {
    setNombre('');
    setApellido('');
    setPlaca('');
    setAutorizadoPor('');
    setSugerenciasAutorizado([]);
    setMostrarSugerenciasAutorizado(false);
    setObservaciones('');
    setFotoArchivo(null);
    setFotoPreview('');
    setFirmaArchivo(null);
    setFirmaPreview('');
    setIsFirmaModalOpen(false);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setFieldErrors({});
    setError('');
  }, []);

  // Obtener configuración de firma
  const fetchConfigFirma = useCallback(async () => {
    if (!user?.id_generado) return;

    try {
      const url = new URL(getApiUrl('BITACORA', 'CONFIG_FIRMA_VEHICULO'));
      url.searchParams.set('idGenerado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      console.log('[RegistrarVehiculo] Configuración de firma recibida:', data);

      if (response.ok && data.success) {
        console.log('[RegistrarVehiculo] requiereFirma:', data.data.requiereFirma);
        setRequiereFirma(data.data.requiereFirma);
      } else {
        console.warn('[RegistrarVehiculo] Error en respuesta de configuración de firma:', data);
      }
    } catch (err) {
      console.error('Error cargando configuración de firma:', err);
      // En caso de error, mantener el valor por defecto (true)
    }
  }, [user?.id_generado]);

  // Obtener configuración de foto
  const fetchConfigFoto = useCallback(async () => {
    if (!user?.id_generado) return;

    try {
      const url = new URL(getApiUrl('BITACORA', 'CONFIG_FOTO_VEHICULO'));
      url.searchParams.set('idGenerado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      console.log('[RegistrarVehiculo] Configuración de foto recibida:', data);

      if (response.ok && data.success) {
        console.log('[RegistrarVehiculo] requiereFoto:', data.data.requiereFoto);
        setRequiereFoto(data.data.requiereFoto);
      } else {
        console.warn('[RegistrarVehiculo] Error en respuesta de configuración de foto:', data);
      }
    } catch (err) {
      console.error('Error cargando configuración de foto:', err);
      // En caso de error, mantener el valor por defecto (true)
    }
  }, [user?.id_generado]);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) return;

      if (!hasPuestoAsignado) {
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        setStatus(null);
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');
      setSuccess('');

      try {
        const url = new URL(getApiUrl('BITACORA', 'STATUS'));
        url.searchParams.set('idGenerado', user.id_generado);
        url.searchParams.set('username', user.username || '');
        url.searchParams.set('appid', user.appid || '');

        const response = await fetch(url.toString());
        const data = await response.json();

        if (response.ok && data.success) {
          setStatus(data.data);
          setLastBitacoraId(null);
        } else {
          setStatus(null);
          setError(data.error || 'No fue posible obtener el estado de la bitácora.');
        }
      } catch (err) {
        console.error('Error cargando estado de bitácora:', err);
        setStatus(null);
        setError('Error de conexión al cargar el estado de la bitácora.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user, hasPuestoAsignado]
  );

  useEffect(() => {
    fetchStatus();
    fetchConfigFirma();
    fetchConfigFoto();
  }, [fetchStatus, fetchConfigFirma, fetchConfigFoto]);

  useEffect(() => {
    if (!fotoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(fotoPreview);
    };
  }, [fotoPreview]);

  useEffect(() => {
    if (!firmaPreview) return undefined;
    return () => {
      URL.revokeObjectURL(firmaPreview);
    };
  }, [firmaPreview]);

  useEffect(() => {
    if (!isFirmaModalOpen) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
  }, [isFirmaModalOpen]);

  const optimizeImage = useCallback((file, { maxWidth = 1024, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      reader.onerror = reject;

      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo optimizar la imagen.'));
              return;
            }
            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const optimizedFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        setFotoArchivo(null);
        setFotoPreview('');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten imágenes para la identificación del conductor.');
        event.target.value = '';
        return;
      }

      let finalFile = file;
      const ONE_MB = 1024 * 1024;

      try {
        if (file.size > ONE_MB) {
          const optimized = await optimizeImage(file, { maxWidth: 1024, quality: 0.7 });
          console.log(`Original: ${(file.size / 1024).toFixed(1)} KB`);
          console.log(`Optimizada: ${(optimized.size / 1024).toFixed(1)} KB`);
          finalFile = optimized;
        } else {
          console.log(`Imagen menor a 1MB, se enviará sin cambios (${(file.size / 1024).toFixed(1)} KB).`);
        }
      } catch (optimizationError) {
        console.error('Error optimizando imagen:', optimizationError);
        setError('No se pudo optimizar la imagen. Intenta con otra foto.');
        event.target.value = '';
        return;
      }

      setFotoArchivo(finalFile);
      setFotoPreview(URL.createObjectURL(finalFile));
      // Limpiar error de foto si existe
      setFieldErrors((prev) => {
        if (prev.foto) {
          const next = { ...prev };
          delete next.foto;
          return next;
        }
        return prev;
      });
    },
    [optimizeImage]
  );

  const handleOpenFirmaModal = useCallback(() => {
    if (submitting || status?.puedeBitacora !== 'SI') return;
    setError('');
    setIsFirmaModalOpen(true);
  }, [status?.puedeBitacora, submitting]);

  const handleCloseFirmaModal = useCallback(() => {
    setIsFirmaModalOpen(false);
    setIsDrawing(false);
    setFirmaTieneTrazo(false);
  }, []);

  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX ?? 0) - rect.left) * (canvas.width / rect.width);
    const y = ((event.clientY ?? 0) - rect.top) * (canvas.height / rect.height);
    return { x, y };
  }, []);

  const handleFirmaPointerDown = useCallback((event) => {
    if (!isFirmaModalOpen || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setFirmaTieneTrazo(true);
  }, [getCanvasCoordinates, isFirmaModalOpen]);

  const handleFirmaPointerMove = useCallback((event) => {
    if (!isDrawing || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCanvasCoordinates, isDrawing]);

  const handleFirmaPointerUp = useCallback((event) => {
    if (!canvasRef.current) return;
    event?.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
  }, []);

  const handleClearFirma = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, []);

  const handleGuardarFirma = useCallback(() => {
    if (!canvasRef.current) return;
    if (!firmaTieneTrazo) {
      setError('Por favor registra la firma del conductor.');
      return;
    }
    setError('');
    canvasRef.current.toBlob((blob) => {
      if (!blob) {
        setError('No se pudo generar la firma. Intenta nuevamente.');
        return;
      }
      const file = new File([blob], `firma_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      if (firmaPreview) {
        URL.revokeObjectURL(firmaPreview);
      }
      const previewUrl = URL.createObjectURL(blob);
      setFirmaArchivo(file);
      setFirmaPreview(previewUrl);
      setIsFirmaModalOpen(false);
      // Limpiar error de firma si existe
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.firma;
        return next;
      });
    }, 'image/jpeg', 0.9);
  }, [firmaPreview, firmaTieneTrazo]);

  const handleEliminarFirma = useCallback(() => {
    if (firmaPreview) {
      URL.revokeObjectURL(firmaPreview);
    }
    setFirmaArchivo(null);
    setFirmaPreview('');
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, [firmaPreview]);

  const scrollToField = (fieldId) => {
    const field = document.getElementById(fieldId);
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus();
      // Resaltar el campo por un momento
      field.classList.add('ring-2', 'ring-red-500');
      setTimeout(() => {
        field.classList.remove('ring-2', 'ring-red-500');
      }, 2000);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!status) return;

    if (status.puedeBitacora !== 'SI') {
      setError('La bitácora está deshabilitada. Primero debes relevar el puesto.');
      return;
    }

    // Validar todos los campos obligatorios
    const errors = {};
    let firstErrorField = null;

    if (!nombre.trim()) {
      errors.nombre = 'El nombre es obligatorio';
      if (!firstErrorField) firstErrorField = 'vehiculo-nombre';
    }

    if (!apellido.trim()) {
      errors.apellido = 'El apellido es obligatorio';
      if (!firstErrorField) firstErrorField = 'vehiculo-apellido';
    }

    if (!placa.trim()) {
      errors.placa = 'La placa es obligatoria';
      if (!firstErrorField) firstErrorField = 'vehiculo-placa';
    }

    if (!autorizadoPor.trim()) {
      errors.autorizadoPor = 'La persona que autoriza es obligatoria';
      if (!firstErrorField) firstErrorField = 'vehiculo-autorizado';
    }

    if (!observaciones || !observaciones.trim()) {
      errors.observaciones = 'Las observaciones son obligatorias';
      if (!firstErrorField) firstErrorField = 'vehiculo-observaciones';
    }

    // Solo validar foto si es requerida
    if (requiereFoto && !fotoArchivo) {
      errors.foto = 'La evidencia fotográfica es obligatoria';
      if (!firstErrorField) firstErrorField = capturaInputId + '-button';
    }

    // Solo validar firma si es requerida
    if (requiereFirma && !firmaArchivo) {
      errors.firma = 'La firma es obligatoria';
      if (!firstErrorField) firstErrorField = 'firma-section';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      
      // Construir mensaje de error detallado
      const camposFaltantes = Object.keys(errors).map(key => {
        const nombres = {
          nombre: 'Nombre',
          apellido: 'Apellido',
          placa: 'Placa',
          autorizadoPor: 'Persona que autoriza',
          observaciones: 'Observaciones',
          foto: 'Evidencia Fotográfica',
          firma: 'Firma'
        };
        return nombres[key];
      }).join(', ');
      
      setError(`Por favor completa los siguientes campos obligatorios: ${camposFaltantes}.`);
      
      // Hacer scroll al primer campo con error
      if (firstErrorField) {
        setTimeout(() => {
          scrollToField(firstErrorField);
        }, 100);
      }
      
      return;
    }

    // Limpiar errores si todo está bien
    setFieldErrors({});

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('idGenerado', status.idGenerado);
      formData.append('tipo', 'VEHICULO');
      formData.append('observaciones', observaciones);
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      formData.append('vehiculoPlaca', placa.trim());
      formData.append('conductorNombre', nombre.trim());
      formData.append('conductorApellido', apellido.trim());
      formData.append('autorizadoPor', autorizadoPor.trim());
      if (fotoArchivo) {
        formData.append('foto', fotoArchivo);
      }
      if (firmaArchivo) {
        formData.append('firma', firmaArchivo);
      }

      const response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const successMessage = data.message || 'VEHÍCULO registrado.';
        const bitacoraId = data.bitacoraId || null;
        
        // Guardar información del vehículo antes de limpiar el formulario
        setVehiculoInfoModal({ placa, nombre, apellido });
        
        // Establecer los valores del éxito y mostrar el modal
        setSuccess(successMessage);
        setLastBitacoraId(bitacoraId);
        setShowSuccessModal(true);
        
        console.log('Modal de éxito configurado:', { 
          showSuccessModal: true, 
          success: successMessage, 
          bitacoraId 
        });
        
        // Limpiar el formulario inmediatamente (no afecta el modal)
        resetForm();
        
        // fetchStatus se llamará después de cerrar el modal
      } else {
        setError(data.error || 'No fue posible registrar el vehículo.');
      }
    } catch (err) {
      console.error('Error registrando vehículo:', err);
      setError('Error de conexión al registrar el vehículo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseErrorModal = useCallback(() => {
    setError('');
  }, []);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
    setVehiculoInfoModal({ placa: '', nombre: '', apellido: '' });
    // Actualizar el status después de cerrar el modal
    if (user && status) {
      fetchStatus(false).catch(err => {
        console.error('Error actualizando status:', err);
      });
    }
  }, [user, status, fetchStatus]);

  const renderEventosRecientes = () => {
    if (!status?.eventosRecientes || status.eventosRecientes.length === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6">
          <p className="text-sm text-gray-600">No hay eventos recientes registrados.</p>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Últimos eventos</h2>
          <p className="text-sm text-gray-500">
            Mostrando los 10 registros más recientes del puesto.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Evento
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hora
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Observación
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {status.eventosRecientes.map((evento, index) => (
                <tr key={`${evento.tipo_evento}-${evento.hora_registro_servidor}-${index}`}>
                  <td className="px-4 py-3 text-sm font-medium text-indigo-600">{evento.tipo_evento}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {evento.hora_ingreso || new Date(evento.hora_registro_servidor).toLocaleTimeString('es-EC', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{evento.observacion || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Registrar vehículo</h1>
            <p className="text-gray-600">
              Registra el ingreso de vehículos en la bitácora del puesto.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchStatus(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={loading || refreshing || submitting}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Actualizando...</span>
              </span>
            ) : (
              'Actualizar estado'
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando estado de la bitácora...</span>
            </div>
          </div>
        ) : (
          <>
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 space-y-1">
                <p className="text-sm font-medium">{success}</p>
                {lastBitacoraId && (
                  <p className="text-xs text-green-700">
                    ID de bitácora generado: <span className="font-semibold">{lastBitacoraId}</span>
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vehiculo-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del conductor <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="vehiculo-nombre"
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.nombre ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ingresa el nombre"
                    value={nombre}
                    onChange={(e) => {
                      setNombre(e.target.value);
                      if (fieldErrors.nombre) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.nombre;
                          return next;
                        });
                      }
                    }}
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                    required
                  />
                  {fieldErrors.nombre && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {fieldErrors.nombre}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="vehiculo-apellido" className="block text-sm font-medium text-gray-700 mb-1">
                    Apellido del conductor <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="vehiculo-apellido"
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.apellido ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ingresa el apellido"
                    value={apellido}
                    onChange={(e) => {
                      setApellido(e.target.value);
                      if (fieldErrors.apellido) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.apellido;
                          return next;
                        });
                      }
                    }}
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                    required
                  />
                  {fieldErrors.apellido && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {fieldErrors.apellido}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vehiculo-placa" className="block text-sm font-medium text-gray-700 mb-1">
                    Placa del vehículo <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="vehiculo-placa"
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 uppercase ${
                      fieldErrors.placa ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ej. PBC-1234"
                    value={placa}
                    onChange={(e) => {
                      setPlaca(e.target.value.toUpperCase());
                      if (fieldErrors.placa) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.placa;
                          return next;
                        });
                      }
                    }}
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                    required
                  />
                  {fieldErrors.placa && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {fieldErrors.placa}
                    </p>
                  )}
                </div>
              <div className="relative" ref={sugerenciasAutorizadoRef}>
                <label htmlFor="vehiculo-autorizado" className="block text-sm font-medium text-gray-700 mb-1">
                  Persona que autoriza el ingreso <span className="text-red-500">*</span>
                </label>
                <input
                  id="vehiculo-autorizado"
                  type="text"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.autorizadoPor ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ingresa quien autoriza"
                  value={autorizadoPor}
                  onChange={(e) => {
                    setAutorizadoPor(e.target.value);
                    if (fieldErrors.autorizadoPor) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.autorizadoPor;
                        return next;
                      });
                    }
                  }}
                  onFocus={() => {
                    if (autorizadoPor.trim().length >= 2 && sugerenciasAutorizado.length > 0) {
                      setMostrarSugerenciasAutorizado(true);
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                  required
                />
                {fieldErrors.autorizadoPor && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.autorizadoPor}
                  </p>
                )}
                
                {/* Dropdown de sugerencias para "Persona que autoriza" */}
                {mostrarSugerenciasAutorizado && sugerenciasAutorizado.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {sugerenciasAutorizado.map((funcionario) => (
                      <button
                        key={funcionario.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none transition-colors"
                        onClick={() => handleSeleccionarSugerenciaAutorizado(funcionario)}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-900">{funcionario.Dato}</span>
                          {funcionario.Descripcion && (
                            <span className="text-xs text-gray-500 ml-auto">({funcionario.Descripcion})</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              </div>

              <div id="foto-section">
                <label htmlFor="vehiculo-foto" className="block text-sm font-medium text-gray-700 mb-1">
                  Evidencia Fotográfica {requiereFoto ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-500 text-xs font-normal">(Opcional)</span>
                  )}
                </label>
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      id={`${capturaInputId}-button`}
                      type="button"
                      onClick={() => document.getElementById(capturaInputId)?.click()}
                      className="inline-flex justify-center items-center w-full sm:w-[140px] px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                      disabled={submitting || status?.puedeBitacora !== 'SI'}
                    >
                      Capturar foto
                    </button>
                  </div>
                  {fieldErrors.foto && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {fieldErrors.foto}
                    </p>
                  )}

                  <input
                    id={capturaInputId}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                  />
                </div>
                  {fotoPreview && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-1">Previsualización:</p>
                      <img
                        src={fotoPreview}
                      alt="Evidencia Fotográfica"
                        className="w-full max-w-xs rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>

              <div id="firma-section">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firma del conductor {requiereFirma ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-500 text-xs font-normal">(Opcional)</span>
                  )}
                  </label>
                {fieldErrors.firma && (
                  <p className="text-xs text-red-600 flex items-center gap-1 mb-2">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.firma}
                  </p>
                )}
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleOpenFirmaModal}
                      className="inline-flex justify-center items-center w-full sm:w-[140px] px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-60"
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                    >
                      Capturar firma
                    </button>
                    {firmaArchivo && (
                      <button
                        type="button"
                        onClick={handleEliminarFirma}
                        className="inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        disabled={submitting}
                      >
                        Eliminar firma
                      </button>
                    )}
                  </div>

                  {firmaPreview ? (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Firma registrada:</p>
                      <img
                        src={firmaPreview}
                        alt="Firma del conductor"
                        className="w-full max-w-xs rounded-lg border border-gray-200 bg-white"
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Solicita al conductor que firme. Esta firma se guardará junto con el registro.</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="vehiculo-observaciones" className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="vehiculo-observaciones"
                  rows={3}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.observaciones ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ej. Entrega de mercancía, revisión de instalaciones, servicio técnico, recogida de material, etc."
                  value={observaciones}
                  onChange={(e) => {
                    setObservaciones(e.target.value);
                    if (fieldErrors.observaciones) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.observaciones;
                        return next;
                      });
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                  required
                />
                {fieldErrors.observaciones && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.observaciones}
                  </p>
                )}
              </div>

              {status?.puedeBitacora !== 'SI' && (
                <p className="text-xs text-red-600 font-medium">
                  Bitácora deshabilitada, primero debe relevar el puesto.
                </p>
              )}

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Guardando...</span>
                    </span>
                  ) : (
                    'Registrar vehículo'
                  )}
                </button>
              </div>
            </form>

            {isFirmaModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Firma del conductor</h3>
                      <p className="text-sm text-gray-500">Pide al conductor que firme dentro del recuadro.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseFirmaModal}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                      aria-label="Cerrar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="border border-dashed border-gray-300 rounded-xl bg-gray-100 p-3">
                      <canvas
                        ref={canvasRef}
                        width={800}
                        height={320}
                        className="w-full h-48 sm:h-64 bg-white border border-gray-300 rounded-lg shadow-inner"
                        style={{ touchAction: 'none' }}
                        onPointerDown={handleFirmaPointerDown}
                        onPointerMove={handleFirmaPointerMove}
                        onPointerUp={handleFirmaPointerUp}
                        onPointerLeave={handleFirmaPointerUp}
                        onPointerCancel={handleFirmaPointerUp}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={handleClearFirma}
                        className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Limpiar firma
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleCloseFirmaModal}
                          className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleGuardarFirma}
                          className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          Guardar firma
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {renderEventosRecientes()}
          </>
        )}
      </div>

      {Boolean(error) && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-red-100 bg-red-600 text-white">
              <h3 className="text-lg font-semibold">No se pudo completar la acción</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">{error}</p>
              <p className="text-xs text-gray-500">Revisa la información solicitada y vuelve a intentarlo.</p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseErrorModal}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseSuccessModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con icono de éxito */}
            <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Registro exitoso</h3>
                  <p className="text-sm text-gray-600 mt-0.5">Vehículo registrado correctamente</p>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="px-6 py-5 space-y-4">
              {/* Información del vehículo */}
              {(vehiculoInfoModal.placa || vehiculoInfoModal.nombre || vehiculoInfoModal.apellido) && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    {vehiculoInfoModal.placa && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm font-semibold text-gray-700">Placa:</span>
                        <span className="text-sm font-bold text-indigo-600">{vehiculoInfoModal.placa}</span>
                      </div>
                    )}
                    {(vehiculoInfoModal.nombre || vehiculoInfoModal.apellido) && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">{`${vehiculoInfoModal.nombre} ${vehiculoInfoModal.apellido}`.trim() || '—'}</span>
                      </div>
                    )}
                    {lastBitacoraId && (
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                        <span className="text-xs text-gray-500">ID: <span className="font-mono font-semibold">{lastBitacoraId}</span></span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mensaje de confirmación */}
              <div className="text-center">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {success || 'El vehículo ha sido registrado correctamente en la bitácora del puesto.'}
                </p>
              </div>

              {/* Indicador visual */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Registro confirmado</span>
              </div>
            </div>

            {/* Footer con botón */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseSuccessModal}
                className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RegistrarVehiculo;
